// Modifier pool for this prototype — a small working subset of the full brainstormed
// list in design/03_Rune_Language.md (modifiers haven't been through the reduction
// pass concepts/operators have; this is just enough to make the resolution engine's
// outputs vary meaningfully).
//
// Decided: every modifier is selectable regardless of the current operator/concept.
// Restricting which modifiers even show up was itself a kind of spoiler — it told the
// player in advance which combinations the engine already knows matter, undermining
// discovery. If a modifier does nothing for a given verb/noun, that's for the player
// to find out by trying it, the same as any other "most combinations do nothing"
// case (see design/03_Rune_Language.md).
//
// `Instant` was removed outright, not just left unfiltered: it is never read by
// resolve.ts under any operator. Every calculation already assumes a one-time
// quantity by default, so `Instant` was never anything but the absence of
// `Continuous`.
//
// `Medium` removed the same way (2026-07-28): magnitudeScale() in resolve.ts only
// ever checks for `unbound`/`precise` (`large`/`small` at the time) — omitting both already
// *is* Medium (×10), so the chip was provably a no-op even when explicitly
// selected, not just unused. `Large` renamed to `Unbound` the same day — a name
// that actually explains why this tier forces the draw through and costs Health on
// a shortfall, not just that it's big. The id was kept as `large` at the time, on
// the reasoning that only a display name had changed and nothing else needed to
// move; **that was wrong and was corrected on 2026-08-14**, when the id became
// `unbound` too. An id is not private — it is what a room's `puzzle.runes` lists,
// what `interact.giveRuneId` grants, what a recording stores, and what the Puzzle
// Analysis rune picker prints on a checkbox. `runes/legacy.ts` migrates the old
// spelling wherever an id crosses back in from storage.
//
// 2026-08-04: `Linear` and `Maximum` removed, `Small` renamed to `Precise`.
// `Linear` was never read by resolve.ts under any operator (Project's own `radial`
// flag already treats its own absence as "linear" by default — same shape as
// Medium's removal) — the word survives as descriptive prose for that default
// beam shape (see e.g. sim/cast.ts's comments), it's just no longer a selectable
// chip pretending to be one. `Maximum` was real in resolve.ts's own text (the
// Bind/Separate·Mass/Charge fission-scale branch) but never reached sim/cast.ts —
// it changed this design tool's narration, never a real object/player state — so
// it's cut rather than kept half-built; its `precision` category had no other
// member and is removed along with it. `Small` becomes `Precise` — reusing a name design/03_Rune_Language.md
// explicitly warned not to reuse, on the grounds that `Precise` already meant
// "not Maximum" for Bind/Separate's boundary choice. That warning was
// conditioned on `Maximum` still existing to collide with; with `Maximum` gone in
// the same pass, the meaning it warned about no longer exists either. Flagged in
// 03_Rune_Language.md rather than silently overridden — see that file's Modifier
// Set section.

export type ModifierCategory = 'direction' | 'magnitude' | 'timing' | 'spatial';

export interface ModifierDef {
  id: string;
  name: string;
  category: ModifierCategory;
  description: string;
}

export const MODIFIERS: ModifierDef[] = [
  // Direction/sign, unified into one modifier (2026-07-26): every cast already
  // has a default directionality it assumes with no modifier present -- Transfer
  // defaults caster-to-target, Entropy defaults to Increase (the cheap direction,
  // per the Second Law) -- so a single `Reverse` flag, meaning "invert whatever
  // that default is," covers both cases without needing a separate rune per
  // pairing. `Self → Target` and `Increase` were previously spelled out as their
  // own modifiers, but neither ever did anything a plain absence of `Reverse`
  // didn't already mean -- both were removed rather than kept as inert options.
  {
    id: 'reverse',
    name: 'Reverse',
    category: 'direction',
    description:
      'Inverts whatever directionality this cast would otherwise default to. Under Transfer, swaps which locus gives and which receives (caster and target, or a marked source and its target). Under Entropy (Transfer or Project), pushes the quantity down instead of up — the expensive direction, since creating order costs Energy the cheap direction doesn\'t.',
  },
  // Magnitude — see the top-of-file note on Medium's removal, Large's rename, and
  // (2026-08-04) Small's rename to Precise.
  { id: 'precise', name: 'Precise', category: 'magnitude', description: '×1 scale — the deliberately restrained case, opposite Unbound.' },
  {
    id: 'unbound',
    name: 'Unbound',
    category: 'magnitude',
    description: '×100 scale. Forces the draw through and costs Health if the caster\'s own Energy comes up short.',
  },
  // Timing — Instant removed: every calculation already assumes a one-time quantity
  // by default, so it was never anything but the absence of Continuous.
  {
    id: 'continuous',
    name: 'Continuous',
    category: 'timing',
    description: 'Applied steadily over time — same total cost, lower peak power.',
  },
  // Spatial — Linear removed 2026-08-04 (see top-of-file note); Radial's own
  // absence already means "linear" by default, so this is the only spatial chip
  // left.
  {
    id: 'radial',
    name: 'Radial',
    category: 'spatial',
    description: 'Spread outward in all directions from a point. Costs more than a directed line for the same intensity.',
  },
  // Oscillating describes a *temporal* pattern (periodic vs. static/one-shot), not a
  // spatial one — it belongs with Continuous, not with Radial. It stays
  // independently toggleable (not grouped into a mutually-exclusive set with
  // Continuous) because "sustained" and "periodic" aren't a presence/absence pair
  // the way Increase/Decrease or Radial/its absence are: a cast can be continuous
  // *and* oscillating (AC current held on) or continuous and not (a steady DC
  // transfer), unlike Reverse or Radial, which really are one-or-the-other choices.
  {
    id: 'oscillating',
    name: 'Oscillating',
    category: 'timing',
    description:
      'Periodic rather than static or one-shot — alternates or repeats over time. Under Project + Charge this is electromagnetic radiation; under Transfer + Charge, alternating current; under Transfer + Momentum, vibration; under Convert Energy<->Momentum, resonance; under Measure, repeated sampling.',
  },
];
